/**
 * Answer control for a BPMN user task with `tri:uxElement="form"`: several fields at once, whose
 * definition (labels, option lists, suggested values) the server builds from the tool the process
 * model names — e.g. the "Lead-Daten" form of Dr. Sasse's tender → Odoo workflow.
 *
 * Only a convenience: the server validates the answer against the definition it stored for this
 * task (tri-server docs/bpm-form-task.md). Client-side checks just keep the send button honest.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronUp, ChevronsUpDown, Loader2, Plus, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import type { TaskForm, TaskFormField, TaskFormOption } from '../UserAPI';

export type TaskFormValues = Record<string, unknown>;

export interface TaskFormAnswerProps {
    loadForm: () => Promise<TaskForm>;
    onSubmit: (values: TaskFormValues) => void;
    disabled?: boolean;
}

const isEmpty = (value: unknown) =>
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);

/** Options still on offer given the other answers (a team bound to one company, say). */
export function offeredOptions(field: TaskFormField, values: TaskFormValues): TaskFormOption[] {
    return (field.options ?? []).filter((option) => {
        if (!option.onlyWhen) return true;
        const other = values[option.onlyWhen.field];
        return !isEmpty(other) && option.onlyWhen.values.some((v) => String(v) === String(other));
    });
}

/**
 * Drops choices that are no longer on offer — a team whose company is not (or no longer) chosen.
 * Fields are visited in order, so a dependency sees the already-pruned value it depends on.
 */
export function pruneUnoffered(form: TaskForm, values: TaskFormValues): TaskFormValues {
    const next = { ...values };
    for (const field of form.fields) {
        if (field.type !== 'select' && field.type !== 'multiselect') continue;
        const offered = offeredOptions(field, next);
        const v = next[field.key];
        if (field.type === 'select' && !isEmpty(v) && !offered.some((o) => String(o.value) === String(v))) {
            delete next[field.key];
        }
        if (field.type === 'multiselect' && Array.isArray(v)) {
            next[field.key] = v.filter((item) => offered.some((o) => String(o.value) === String(item)));
        }
    }
    return next;
}

/** Pre-filled values: the suggestion where it is on offer, a new-value text for an allowNew select. */
export function initialValues(form: TaskForm): TaskFormValues {
    const values: TaskFormValues = {};
    for (const field of form.fields) {
        if (field.value !== undefined && field.value !== null) values[field.key] = field.value;
        if (field.allowNew && field.newValueKey && field.newValue && isEmpty(values[field.key])) {
            values[field.newValueKey] = field.newValue;
        }
    }
    return pruneUnoffered(form, values);
}

/** How many fields carry an answer — for the header of a collapsed form. */
export function filledCount(form: TaskForm, values: TaskFormValues): number {
    return form.fields.filter((field) =>
        !isEmpty(values[field.key]) || Boolean(field.allowNew && field.newValueKey && !isEmpty(values[field.newValueKey])),
    ).length;
}

/** Required fields still without an answer — an allowNew select counts a typed new value. */
export function missingRequired(form: TaskForm, values: TaskFormValues): TaskFormField[] {
    return form.fields.filter((field) => {
        if (!field.required) return false;
        if (!isEmpty(values[field.key])) return false;
        return !(field.allowNew && field.newValueKey && !isEmpty(values[field.newValueKey]));
    });
}

interface PickerProps {
    field: TaskFormField;
    options: TaskFormOption[];
    values: TaskFormValues;
    disabled?: boolean;
    onChange: (patch: TaskFormValues) => void;
}

/** Searchable single/multi select; offers "create new" for an allowNew select. */
const OptionPicker: React.FC<PickerProps> = ({ field, options, values, disabled, onChange }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const multi = field.type === 'multiselect';
    const current = values[field.key];
    const chosen = multi ? (Array.isArray(current) ? current : []) : current;
    const newText = field.newValueKey ? values[field.newValueKey] : undefined;

    const labelOf = (value: unknown) => options.find((o) => String(o.value) === String(value))?.label ?? String(value);
    const summary = multi
        ? (chosen as unknown[]).map(labelOf).join(', ')
        : !isEmpty(chosen)
            ? labelOf(chosen)
            : typeof newText === 'string' && newText
                ? `${newText} (${t('chatBotPage.formNew', { defaultValue: 'neu' })})`
                : '';

    const pick = (option: TaskFormOption) => {
        if (multi) {
            const list = chosen as unknown[];
            const has = list.some((v) => String(v) === String(option.value));
            onChange({ [field.key]: has ? list.filter((v) => String(v) !== String(option.value)) : [...list, option.value] });
        } else {
            onChange({ [field.key]: option.value, ...(field.newValueKey && { [field.newValueKey]: null }) });
            setOpen(false);
        }
    };
    const createNew = () => {
        if (!field.newValueKey || !search.trim()) return;
        onChange({ [field.key]: null, [field.newValueKey]: search.trim() });
        setOpen(false);
    };
    const exactHit = options.some((o) => o.label.toLowerCase() === search.trim().toLowerCase());

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label={field.label}
                    data-testid={`task-form-${field.key}`}
                    disabled={disabled}
                    className="h-auto min-h-8 w-full justify-between whitespace-normal text-left font-normal"
                >
                    <span className={summary ? '' : 'text-muted-foreground'}>
                        {summary || t('chatBotPage.selectPlaceholder', { defaultValue: 'Bitte auswählen' })}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[280px] p-0" align="start">
                <Command>
                    <CommandInput
                        placeholder={t('chatBotPage.formSearch', { defaultValue: 'Suchen …' })}
                        value={search}
                        onValueChange={setSearch}
                    />
                    <CommandList>
                        <CommandEmpty>{t('chatBotPage.formNoMatch', { defaultValue: 'Kein Treffer.' })}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => {
                                const selected = multi
                                    ? (chosen as unknown[]).some((v) => String(v) === String(option.value))
                                    : String(chosen) === String(option.value);
                                return (
                                    <CommandItem key={String(option.value)} value={`${option.label} ${option.value}`} onSelect={() => pick(option)}>
                                        <Check className={`mr-2 h-4 w-4 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                                        {option.label}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                        {field.allowNew && search.trim() && !exactHit && (
                            <CommandGroup>
                                <CommandItem value={`__new__ ${search}`} onSelect={createNew} data-testid={`task-form-${field.key}-new`}>
                                    <Plus className="mr-2 h-4 w-4" />
                                    {t('chatBotPage.formCreateNew', { defaultValue: '„{{text}}" neu anlegen', text: search.trim() })}
                                </CommandItem>
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

/** Felder, die in der zweispaltigen Ansicht die ganze Breite brauchen: Freitext und Mehrfachauswahl. */
export const isWideField = (field: { type?: string }): boolean =>
    field.type === 'textarea' || field.type === 'multiselect';

export const TaskFormAnswer: React.FC<TaskFormAnswerProps> = ({ loadForm, onSubmit, disabled }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState<TaskForm | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [values, setValues] = useState<TaskFormValues>({});
    const [attempt, setAttempt] = useState(0);
    // Einklappbar: Neben einem angedockten Dokument auf einem 14-Zoll-Bildschirm nahm das Formular
    // fast die ganze Chathöhe ein. Eingeklappt bleibt es mit seinen Werten stehen.
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setForm(null);
        setError(null);
        loadForm()
            .then((loaded) => {
                if (cancelled) return;
                setForm(loaded);
                setValues(initialValues(loaded));
            })
            .catch((err: any) => {
                if (!cancelled) setError(err?.message || '');
            });
        return () => {
            cancelled = true;
        };
    }, [loadForm, attempt]);

    const retry = useCallback(() => setAttempt((n) => n + 1), []);

    /** A change can take options away from later fields (the team of another company). */
    const change = useCallback(
        (patch: TaskFormValues) => {
            setValues((prev) => (form ? pruneUnoffered(form, { ...prev, ...patch }) : { ...prev, ...patch }));
        },
        [form],
    );

    const missing = useMemo(() => (form ? missingRequired(form, values) : []), [form, values]);

    if (error !== null) {
        return (
            <div className="flex flex-1 items-center justify-center gap-2 text-sm text-destructive" data-testid="task-form-error">
                <span>{error || t('chatBotPage.formLoadFailed', { defaultValue: 'Das Formular konnte nicht geladen werden.' })}</span>
                <Button type="button" variant="outline" size="sm" onClick={retry} aria-label={t('chatBotPage.selectRetry', { defaultValue: 'Erneut laden' })}>
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>
        );
    }
    if (form === null) {
        return (
            <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="task-form-loading">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('chatBotPage.formLoading', { defaultValue: 'Formular wird vorbereitet …' })}
            </div>
        );
    }

    const sendButton = (
        <Button
            type="button"
            size="sm"
            className="gap-2"
            data-testid="task-form-send"
            disabled={disabled || missing.length > 0}
            onClick={() => onSubmit(values)}
        >
            <Check className="h-4 w-4" />
            {t('chatBotPage.formSubmit', { defaultValue: 'Übernehmen' })}
        </Button>
    );

    return (
        <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-center gap-2">
            <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs font-medium text-muted-foreground hover:text-foreground"
                aria-expanded={!collapsed}
                onClick={() => setCollapsed((c) => !c)}
                data-testid="task-form-toggle"
            >
                {collapsed ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
                <span className="truncate">
                    {t('chatBotPage.formHeader', {
                        defaultValue: 'Formular · {{filled}} von {{total}} Feldern ausgefüllt',
                        filled: filledCount(form, values),
                        total: form.fields.length,
                    })}
                </span>
            </button>
            {collapsed && sendButton}
        </div>
        {!collapsed && (
        // Zwei Spalten ab mittlerer Breite und höchstens 45 % der Fensterhöhe: Das Formular sitzt im
        // Eingabebereich, und bei elf Pflichtfeldern (Lead-Daten) blieb über ihm kaum Platz für den Chat.
        <div
            className="grid max-h-[45vh] flex-1 grid-cols-1 content-start gap-x-4 gap-y-3 overflow-y-auto pr-1 sm:grid-cols-2"
            data-testid="task-form"
        >
            {form.fields.map((field) => {
                const id = `task-form-field-${field.key}`;
                const value = values[field.key];
                return (
                    <div
                        key={field.key}
                        className={`flex min-w-0 flex-col gap-1${isWideField(field) ? ' sm:col-span-2' : ''}`}
                        data-testid={`task-form-cell-${field.key}`}
                    >
                        <label htmlFor={id} className="text-xs font-medium text-foreground">
                            {field.label}
                            {field.required && <span className="ml-0.5 text-destructive">*</span>}
                        </label>
                        {field.type === 'select' || field.type === 'multiselect' ? (
                            <OptionPicker
                                field={field}
                                options={offeredOptions(field, values)}
                                values={values}
                                disabled={disabled}
                                onChange={change}
                            />
                        ) : field.type === 'textarea' ? (
                            <Textarea
                                id={id}
                                rows={4}
                                disabled={disabled}
                                value={typeof value === 'string' ? value : ''}
                                onChange={(event) => change({ [field.key]: event.target.value })}
                            />
                        ) : (
                            <Input
                                id={id}
                                type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                className="h-8"
                                disabled={disabled}
                                value={value === undefined || value === null ? '' : String(value)}
                                onChange={(event) => change({ [field.key]: event.target.value })}
                            />
                        )}
                        {field.hint && <span className="text-xs text-muted-foreground">{field.hint}</span>}
                    </div>
                );
            })}
            <div className="flex items-center justify-end gap-2 pt-1 sm:col-span-2">
                {missing.length > 0 && (
                    <span className="text-xs text-muted-foreground" data-testid="task-form-missing">
                        <X className="mr-1 inline h-3 w-3" />
                        {t('chatBotPage.formMissing', { defaultValue: 'Fehlt noch: {{fields}}', fields: missing.map((f) => f.label).join(', ') })}
                    </span>
                )}
                {sendButton}
            </div>
        </div>
        )}
        </div>
    );
};

export default TaskFormAnswer;
